import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Roving highlight for a search-and-pick dropdown, driven from the search input.
 *
 * Focus stays on the input so typing keeps working; arrow keys move a visual
 * highlight over the options and Enter picks the highlighted one. Callers spread
 * `getItemProps(index)` onto each option and `handleKeyDown` onto the input.
 */
export default function useListKeyboardNav({
  opened,
  itemCount,
  initialIndex = 0,
  onSelect,
  onClose,
}) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const itemRefs = useRef(new Map());
  const openedRef = useRef(opened);
  openedRef.current = opened;
  // Skip the scroll-into-view on the pointer-driven highlight, which would
  // otherwise yank the list out from under the cursor.
  const scrollOnChange = useRef(true);

  useEffect(() => {
    if (opened) setActiveIndex(initialIndex);
    // Re-running on initialIndex would fight the arrow keys once open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // The dropdown mounts a tick after `opened` flips, so focus on ref attach
  // rather than from an effect, where the input does not exist yet.
  const inputRef = useCallback((node) => {
    if (node && openedRef.current) requestAnimationFrame(() => node.focus());
  }, []);

  // Keep the highlight on a real option as the filtered list shrinks.
  useEffect(() => {
    if (activeIndex > itemCount - 1) setActiveIndex(itemCount > 0 ? 0 : -1);
  }, [itemCount, activeIndex]);

  useEffect(() => {
    if (!opened || !scrollOnChange.current) {
      scrollOnChange.current = true;
      return;
    }
    const raf = requestAnimationFrame(() => {
      itemRefs.current.get(activeIndex)?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeIndex, opened]);

  const move = useCallback(
    (delta) => {
      if (itemCount === 0) return;
      setActiveIndex((i) => (i + delta + itemCount) % itemCount);
    },
    [itemCount]
  );

  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          move(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          move(-1);
          break;
        case "Home":
          event.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          event.preventDefault();
          setActiveIndex(itemCount - 1);
          break;
        case "Enter":
          if (activeIndex >= 0 && activeIndex < itemCount) {
            event.preventDefault();
            onSelect(activeIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          onClose?.();
          break;
        default:
          break;
      }
    },
    [move, itemCount, activeIndex, onSelect, onClose]
  );

  const getItemProps = useCallback(
    (index) => ({
      ref: (el) => {
        if (el) itemRefs.current.set(index, el);
        else itemRefs.current.delete(index);
      },
      tabIndex: -1,
      "data-active": index === activeIndex || undefined,
      onMouseMove: () => {
        if (index === activeIndex) return;
        scrollOnChange.current = false;
        setActiveIndex(index);
      },
    }),
    [activeIndex]
  );

  return { activeIndex, setActiveIndex, handleKeyDown, getItemProps, inputRef };
}
